import React, { useState } from "react";
import classes from "./memory.module.css";
import CustomHeader from "../../components/Header/CustomHeader";
import { STRINGS } from "./strings";
import MemoryOption from "./components/MemoryOption";
import SummaryItem from "./components/SummaryItem";
import MemoryEmptyState from "./components/MemoryEmptyState";
import {
  LANDING_PAGE,
  PDF_IMAGE,
  NOTE_ICON,
} from "../../utils/constants/assets";
import { ICON_LABELS, fontWeight, fontSize } from "../../utils/constants/UI";
import CustomModal from "../../components/Modals/CustomModal";
import HtmlEditor from "../../components/Editor/HtmlEditor";
import InputField from "../../components/InputFields/InputField";
import TypographyInline from "../../components/Typography/TypographyInline";
import { BASE, BORDER, GREY, PRIMARY } from "../../utils/constants/colors";
import Button from "../../components/Buttons/Button";
import IconComponent from "../../components/Icon/IconComponent";
import {
  postRequest,
  getRequest,
  patchRequest,
  deleteRequest,
} from "../../Apis/apiWrapper";
import { authEndpoints } from "../../utils/constants/httpConstants";
import { useNavigate } from "react-router-dom";
import Loader from "../../components/Loaders/CircularLoader";
import { useEffect } from "react";
import EventEmitter from "../../utils/emitter/EventEmitter";
import { EMITTER_ERROR } from "../../utils/constants";
import { ERROR } from "../../utils/constants/colors";

const MemoryScreen = ({ isSettings = false }) => {
  const [memoryList, setMemoryList] = useState([]);

  const [modals, setModals] = useState({
    scrape: false,
    pdf: false,
    manual: false,
  });

  const [websiteUrl, setWebsiteUrl] = useState("");
  const [manualContent, setManualContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [generatedSummary, setGeneratedSummary] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenOption, setRegenOption] = useState(null); // 'scrape', 'pdf', 'manual'
  const navigate = useNavigate();

  const toggleModal = (type, value) => {
    setModals((prev) => ({ ...prev, [type]: value }));
  };

  const breadcrumbs = [{ id: "memory", label: STRINGS.MEMORY_HEADER }];

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handleGenerate = async (type, isRegenerate = false) => {
    setLoading(true);
    let payload = { type };

    try {
      if (type === 1) {
        if (!websiteUrl) return;
        let finalUrl = websiteUrl.trim();
        if (!/^https?:\/\//i.test(finalUrl)) {
          finalUrl = `https://${finalUrl}`;
        }
        payload.url = finalUrl;
      } else if (type === 2) {
        if (!selectedFile) return;
        const base64 = await toBase64(selectedFile);
        payload.pdf = base64;
      } else if (type === 3) {
        if (!manualContent) return;
        payload.text = manualContent;
      }

      const endpoint = isRegenerate
        ? authEndpoints.regenerateSummary
        : authEndpoints.generateSummary;

      const response = await postRequest(navigate, endpoint, payload);

      if (response?.data?.status) {
        const newSummary = response.data.data.summary;
        const newType = response.data.data.type || type;
        setGeneratedSummary(newSummary);

        if (isRegenerate) {
          setIsRegenerating(false);
          setRegenOption(null);
          setShowPreview(true);
          // Optional: clear inputs
          setWebsiteUrl("");
          setSelectedFile(null);
          setManualContent("");
        } else if (memoryList.length > 0) {
          setShowPreview(true);
        } else {
          // First time generation - just close modal as per user request
          setShowPreview(false);
          fetchSummary();
        }

        setMemoryList([
          {
            id: "current-summary",
            type: getTypeName(newType),
            title: "Company Knowledge Base",
            description: newSummary,
            rawType: newType,
          },
        ]);
        // Close the input modal
        setModals({ scrape: false, pdf: false, manual: false });
      } else {
        console.error("API Error:", response?.data?.message);
      }
    } catch (error) {
      console.error("Error generating summary:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeName = (type) => {
    switch (type) {
      case 1:
        return "Website";
      case 2:
        return "PDF";
      case 3:
        return "Manual";
      default:
        return "AI Summary";
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await getRequest(
        navigate,
        authEndpoints.getCompanySummary,
      );
      if (response?.data?.status && response.data.data) {
        const { summary, type } = response.data.data;
        if (summary) {
          setMemoryList([
            {
              id: "current-summary",
              type: getTypeName(type),
              title: "Company Knowledge Base",
              description: summary,
              rawType: type,
            },
          ]);
        } else {
          setMemoryList([]);
        }
      }
    } catch (error) {
      console.error("Error fetching summary:", error);
    }
  };

  const handleEditSummary = async () => {
    setLoading(true);
    try {
      const response = await patchRequest(
        navigate,
        authEndpoints.editCompanySummary,
        { summary: generatedSummary },
      );
      if (response?.data?.status) {
        setShowPreview(false);
        fetchSummary(); // Refresh list
      }
    } catch (error) {
      console.error("Error editing summary:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSummary = async () => {
    setLoading(true);
    try {
      const response = await deleteRequest(
        authEndpoints.deleteCompanySummary,
        {},
        navigate,
      );
      if (response?.data?.status) {
        setMemoryList([]);
        setShowDeleteModal(false);
      }
    } catch (error) {
      console.error("Error deleting summary:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOptionClick = (type) => {
    toggleModal(type, true);
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  const modalProps = {
    width: "45vw",
    blurBackdrop: true,
  };

  return (
    <div
      className={`${classes.wrapper} ${isSettings ? classes.settingsWrapper : ""}`}
    >
      {!isSettings && (
        <CustomHeader items={breadcrumbs} description={STRINGS.MEMORY_DESC} />
      )}

      <div
        className={`${classes.mainContainer} ${isSettings ? classes.settingsMainContainer : ""}`}
      >
        {/* Memory Options Row - Move to top for better discovery if empty */}
        <div id="memory-options-section" className={classes.optionsRow}>
          <MemoryOption
            title={
              memoryList.length > 0
                ? STRINGS.SCRAPING_TITLE_ADD
                : STRINGS.SCRAPING_TITLE
            }
            description={
              memoryList.length > 0
                ? STRINGS.SCRAPING_DESC_ADD
                : STRINGS.SCRAPING_DESC
            }
            asset={LANDING_PAGE}
            onClick={() => handleOptionClick("scrape")}
          />
          <MemoryOption
            title={
              memoryList.length > 0 ? STRINGS.PDF_TITLE_ADD : STRINGS.PDF_TITLE
            }
            description={
              memoryList.length > 0 ? STRINGS.PDF_DESC_ADD : STRINGS.PDF_DESC
            }
            asset={PDF_IMAGE}
            onClick={() => handleOptionClick("pdf")}
          />
          <MemoryOption
            title={
              memoryList.length > 0
                ? STRINGS.MANUAL_TITLE_ADD
                : STRINGS.MANUAL_TITLE
            }
            description={
              memoryList.length > 0
                ? STRINGS.MANUAL_DESC_ADD
                : STRINGS.MANUAL_DESC
            }
            asset={NOTE_ICON}
            onClick={() => handleOptionClick("manual")}
          />
        </div>

        {/* Summary Section */}
        <div className={classes.summarySection}>
          {memoryList.length > 0 && (
            <div className={classes.sectionHeader}>
              <TypographyInline
                label="Company Knowledge Base"
                size="xl"
                fontWeight={fontWeight.BOLD}
                color={BASE.BLACK}
              />
            </div>
          )}
          <div className={classes.summaryList}>
            {memoryList.length > 0 ? (
              memoryList.map((item) => (
                <SummaryItem
                  key={item.id}
                  type={item.type}
                  title={item.title}
                  description={item.description}
                  onEdit={() => {
                    setGeneratedSummary(item.description);
                    setShowPreview(true);
                  }}
                  onDelete={() => setShowDeleteModal(true)}
                />
              ))
            ) : (
              <MemoryEmptyState data={STRINGS.EMPTY_DATA} />
            )}
          </div>
        </div>
      </div>

      {/* Scrape Website Modal */}
      <CustomModal
        {...modalProps}
        open={modals.scrape}
        onClose={() => toggleModal("scrape", false)}
        header={{
          heading: { label: STRINGS.SCRAPE_WEBSITE_HEADER },
        }}
        footer={{
          right: (
            <div className={classes.actionButtons}>
              <Button
                label={STRINGS.CANCEL}
                variant="ghost"
                size="sm36"
                onClick={() => toggleModal("scrape", false)}
              />
              <Button
                label={loading ? STRINGS.GENERATING : STRINGS.GENERATE}
                variant="primary"
                size="sm36"
                disabled={loading || !websiteUrl}
                onClick={() => handleGenerate(1, memoryList.length > 0)}
              />
            </div>
          ),
        }}
      >
        {loading && (
          <div className={classes.loaderOverlay}>
            <Loader message={STRINGS.GENERATING} />
          </div>
        )}
        <div className={classes.modalBody}>
          <div className={classes.modalContentWrapper}>
            <TypographyInline
              label={STRINGS.WEBSITE_URL}
              size="md"
              fontWeight={fontWeight.BOLD}
              color={BASE.BLACK}
              isRequired
            />
            <InputField
              placeholder={STRINGS.URL_PLACEHOLDER}
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              size="md40"
            />
          </div>
        </div>
      </CustomModal>

      {/* PDF Upload Modal */}
      <CustomModal
        {...modalProps}
        open={modals.pdf}
        onClose={() => toggleModal("pdf", false)}
        header={{
          heading: { label: STRINGS.UPLOAD_PDF_HEADER },
        }}
        footer={{
          right: (
            <div className={classes.actionButtons}>
              <Button
                label={STRINGS.CANCEL}
                variant="ghost"
                size="sm36"
                onClick={() => toggleModal("pdf", false)}
              />
              <Button
                label={loading ? STRINGS.GENERATING : STRINGS.GENERATE}
                variant="primary"
                size="sm36"
                disabled={loading || !selectedFile}
                onClick={() => handleGenerate(2, memoryList.length > 0)}
              />
            </div>
          ),
        }}
      >
        {loading && (
          <div className={classes.loaderOverlay}>
            <Loader message={STRINGS.GENERATING} />
          </div>
        )}
        <div className={classes.modalBody}>
          <div
            className={classes.fileUploadContainer}
            onClick={() => document.getElementById("pdf-upload-input").click()}
            style={{ cursor: "pointer" }}
          >
            <input
              id="pdf-upload-input"
              type="file"
              accept=".pdf"
              style={{ display: "none" }}
              onChange={(e) => setSelectedFile(e.target.files[0])}
            />
            <IconComponent
              iconLabel={ICON_LABELS.UPLOAD_FILE}
              fontSize="64px"
              color={PRIMARY.W_500}
            />
            <div className={classes.modalContentWrapper}>
              <TypographyInline
                label={selectedFile ? selectedFile.name : STRINGS.SELECT_FILE}
                size="xl"
                fontWeight={fontWeight.BOLD}
                color={BASE.BLACK}
              />
              <TypographyInline
                label="PDF files only (max 10MB)"
                size="sm"
                fontWeight={fontWeight.REGULAR}
                color={GREY.SECONDARY}
              />
            </div>
          </div>
        </div>
      </CustomModal>

      {/* Manual Writing Modal */}
      <CustomModal
        width="60vw"
        blurBackdrop={true}
        open={modals.manual}
        onClose={() => toggleModal("manual", false)}
        header={{
          heading: { label: STRINGS.MANUAL_WRITING_HEADER },
        }}
        footer={{
          right: (
            <div className={classes.actionButtons}>
              <Button
                label={STRINGS.CANCEL}
                variant="ghost"
                size="sm36"
                onClick={() => toggleModal("manual", false)}
              />
              <Button
                label={loading ? STRINGS.GENERATING : STRINGS.GENERATE}
                variant="primary"
                size="sm36"
                disabled={loading || !manualContent}
                onClick={() => handleGenerate(3, memoryList.length > 0)}
              />
            </div>
          ),
        }}
      >
        {loading && (
          <div className={classes.loaderOverlay}>
            <Loader message={STRINGS.GENERATING} />
          </div>
        )}
        <div className={classes.modalBody}>
          <TypographyInline
            label={STRINGS.MANUAL_MODAL_SUBTITLE}
            size="md"
            fontWeight={fontWeight.SEMIBOLD}
            color={BASE.BLACK}
          />
          <div
            style={{
              height: "450px",
              border: `2px solid ${BORDER.LIGHT}`,
              borderRadius: "16px",
              overflow: "hidden",
            }}
          >
            <HtmlEditor
              id="memory-manual-editor"
              onInput={(html) => setManualContent(html)}
              editorHeight="380px"
            />
          </div>
        </div>
      </CustomModal>

      {/* Summary Preview Modal */}
      <CustomModal
        width="60vw"
        blurBackdrop={true}
        open={showPreview}
        onClose={() => {
          setShowPreview(false);
          setIsRegenerating(false);
          setRegenOption(null);
        }}
        header={{
          heading: {
            label: isRegenerating
              ? regenOption
                ? STRINGS[`${regenOption.toUpperCase()}_TITLE`]
                : STRINGS.ADD_MORE
              : STRINGS.SUMMARY_PREVIEW,
          },
        }}
        footer={{
          right: (
            <div className={classes.actionButtons}>
              {isRegenerating ? (
                <>
                  <Button
                    label={STRINGS.BACK_TO_PREVIEW}
                    variant="ghost"
                    size="sm36"
                    onClick={() => {
                      if (regenOption) setRegenOption(null);
                      else setIsRegenerating(false);
                    }}
                  />
                  {regenOption && (
                    <Button
                      label={
                        loading ? STRINGS.GENERATING : STRINGS.UPDATE_EXPAND
                      }
                      variant="primary"
                      size="sm36"
                      disabled={
                        loading ||
                        (regenOption === "scrape" && !websiteUrl) ||
                        (regenOption === "pdf" && !selectedFile) ||
                        (regenOption === "manual" && !manualContent)
                      }
                      onClick={() =>
                        handleGenerate(
                          regenOption === "scrape"
                            ? 1
                            : regenOption === "pdf"
                              ? 2
                              : 3,
                          true,
                        )
                      }
                    />
                  )}
                </>
              ) : (
                <>
                  <Button
                    label={STRINGS.CANCEL}
                    variant="ghost"
                    size="sm36"
                    onClick={() => setShowPreview(false)}
                  />
                  <Button
                    label={loading ? "Saving..." : "Save to Knowledge Base"}
                    variant="primary"
                    size="sm36"
                    disabled={loading}
                    onClick={handleEditSummary}
                  />
                </>
              )}
            </div>
          ),
        }}
      >
        <div className={classes.modalBody}>
          {loading && (
            <div className={classes.loaderOverlay}>
              <Loader message={STRINGS.GENERATING} />
            </div>
          )}
          {!isRegenerating ? (
            <>
              <TypographyInline
                label={STRINGS.PREVIEW_MODAL_SUBTITLE}
                size="md"
                fontWeight={fontWeight.SEMIBOLD}
                color={BASE.BLACK}
              />
              <div
                style={{
                  height: "450px",
                  border: `2px solid ${BORDER.LIGHT}`,
                  borderRadius: "16px",
                  overflow: "hidden",
                }}
              >
                <HtmlEditor
                  id="summary-preview-editor"
                  previosData={generatedSummary}
                  onInput={(html) => setGeneratedSummary(html)}
                  editorHeight="380px"
                />
              </div>
            </>
          ) : !regenOption ? (
            <div className={classes.optionsRow} style={{ padding: "20px 0" }}>
              <MemoryOption
                title={STRINGS.SCRAPING_TITLE}
                description={STRINGS.SCRAPING_DESC}
                asset={LANDING_PAGE}
                onClick={() => setRegenOption("scrape")}
              />
              <MemoryOption
                title={STRINGS.PDF_TITLE}
                description={STRINGS.PDF_DESC}
                asset={PDF_IMAGE}
                onClick={() => setRegenOption("pdf")}
              />
              <MemoryOption
                title={STRINGS.MANUAL_TITLE}
                description={STRINGS.MANUAL_DESC}
                asset={NOTE_ICON}
                onClick={() => setRegenOption("manual")}
              />
            </div>
          ) : (
            <div className={classes.modalContentWrapper}>
              {regenOption === "scrape" && (
                <>
                  <TypographyInline
                    label={STRINGS.WEBSITE_URL}
                    size="md"
                    fontWeight={fontWeight.BOLD}
                    color={BASE.BLACK}
                    isRequired
                  />
                  <InputField
                    placeholder={STRINGS.URL_PLACEHOLDER}
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    size="md40"
                  />
                </>
              )}
              {regenOption === "pdf" && (
                <div
                  className={classes.fileUploadContainer}
                  onClick={() =>
                    document.getElementById("pdf-upload-input-regen").click()
                  }
                  style={{ cursor: "pointer" }}
                >
                  <input
                    id="pdf-upload-input-regen"
                    type="file"
                    accept=".pdf"
                    style={{ display: "none" }}
                    onChange={(e) => setSelectedFile(e.target.files[0])}
                  />
                  <IconComponent
                    iconLabel={ICON_LABELS.UPLOAD_FILE}
                    fontSize="64px"
                    color={PRIMARY.W_500}
                  />
                  <div className={classes.modalContentWrapper}>
                    <TypographyInline
                      label={
                        selectedFile ? selectedFile.name : STRINGS.SELECT_FILE
                      }
                      size="xl"
                      fontWeight={fontWeight.BOLD}
                      color={BASE.BLACK}
                    />
                    <TypographyInline
                      label="PDF files only (max 10MB)"
                      size="sm"
                      fontWeight={fontWeight.REGULAR}
                      color={GREY.SECONDARY}
                    />
                  </div>
                </div>
              )}
              {regenOption === "manual" && (
                <div
                  style={{
                    height: "450px",
                    border: `2px solid ${BORDER.LIGHT}`,
                    borderRadius: "16px",
                    overflow: "hidden",
                    textAlign: "left",
                  }}
                >
                  <HtmlEditor
                    id="memory-manual-editor-regen"
                    onInput={(html) => setManualContent(html)}
                    editorHeight="380px"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </CustomModal>
      {/* Delete Confirmation Modal */}
      <CustomModal
        width="30vw"
        blurBackdrop={true}
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        header={{
          heading: { label: STRINGS.DELETE_MODAL_TITLE },
        }}
        footer={{
          right: (
            <div className={classes.actionButtons}>
              <Button
                label={STRINGS.CANCEL}
                variant="ghost"
                size="sm36"
                onClick={() => setShowDeleteModal(false)}
              />
              <Button
                label={loading ? "Deleting..." : "Delete"}
                variant="primary"
                size="sm36"
                disabled={loading}
                onClick={handleDeleteSummary}
              />
            </div>
          ),
        }}
      >
        <div
          className={classes.modalBody}
          style={{ textAlign: "center", padding: "20px 0" }}
        >
          <IconComponent
            iconLabel={ICON_LABELS.DELETE_OUTLINE}
            fontSize="64px"
            color={ERROR.W_500}
          />
          <TypographyInline
            label={STRINGS.DELETE_MODAL_SUBTITLE}
            size="md"
            fontWeight={fontWeight.REGULAR}
            color={BASE.BLACK}
          />
        </div>
      </CustomModal>
    </div>
  );
};

export default MemoryScreen;
